// ═══════════════════════════════════════════════════════════════════════════
// ADMIN: PARAMÈTRES DU TENANT
// Chantier comptabilisation des usages (mai 2026).
// ═══════════════════════════════════════════════════════════════════════════
//
// Édite la table singleton `tenant_settings` : crédits IA partagés
// « Rédaction / Analyse », rétention des actions, nombre d'utilisateurs
// max, jour de début du mois de comptage.

interface TenantSettingsData {
  code_client: string;
  ia_credits: number;
  actions_retention_months: number;
  notifications_retention_days: number;
  max_users: number;
  month_start_day: number;
  kpi_blocked_orange_pct: number;
  kpi_progress_orange_pct: number;
  kpi_reeval_stable_weeks: number;
  committee_alerts_enabled: boolean;
  // Informations contractuelles : affichées, jamais éditables ici. La saisie
  // se fait dans pmo-admin ; la modification passe par contact@ornexia.fr.
  company_name?: string | null;
  contract_contact_name?: string | null;
  contract_contact_email?: string | null;
  updated_at?: string | null;
}

declare global {
  interface Window {
    renderTenantSettingsPage: typeof renderTenantSettingsPage;
  }
}

pages.adminTenantSettings = async function () {
  setHeader(t('tenantSettings.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const data = (await api.getTenantSettings()) as TenantSettingsData;
    renderTenantSettingsPage(data);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('tenantSettings.err.load');
    setContent(`<div class="error" style="padding:16px">${esc(msg)}</div>`);
  }
};

function renderTenantSettingsPage(data: TenantSettingsData): void {
  const canWrite = !!(window.hasPermission && window.hasPermission('tenant_settings.write'));
  const disabled = canWrite ? '' : 'disabled';

  const fieldStyle =
    'width:160px;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg2);font-size:13px';
  const helpTip = (key: string): string =>
    `<span class="help-tip" tabindex="0" role="img" aria-label="${esc(t(key))}" title="${esc(t(key))}">?</span>`;

  const numField = (
    id: string,
    labelKey: string,
    helpKey: string,
    value: number,
    min: number,
  ): string => `
    <div class="form-group" style="margin-bottom:16px">
      <label style="font-weight:500;display:inline-flex;align-items:center;gap:8px">
        <span>${esc(t(labelKey))}</span>${helpTip(helpKey)}
      </label>
      <div style="margin-top:4px">
        <input type="number" id="${id}" value="${esc(String(value))}" min="${min}" step="1"
          ${disabled} style="${fieldStyle}">
      </div>
    </div>`;

  // Jour de début du mois : select 1-28 (bornes alignées sur le CheckConstraint
  // backend pour éviter les jours absents en février).
  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1)
    .map(
      (d) => `<option value="${d}"${d === data.month_start_day ? ' selected' : ''}>${d}</option>`,
    )
    .join('');

  // Informations contractuelles : lecture seule. Un champ vide affiche « — »
  // plutôt que rien, pour que l'absence se distingue d'un bug d'affichage.
  const roField = (labelKey: string, value: string | null | undefined): string => `
    <div class="form-group" style="margin-bottom:12px">
      <label style="font-weight:500">${esc(t(labelKey))}</label>
      <div style="margin-top:4px;font-size:14px">${esc(value || '—')}</div>
    </div>`;

  const saveBtn = canWrite
    ? `<button class="btn btn-primary" id="btn-ts-save" style="margin-top:8px">${esc(t('tenantSettings.btn.save'))}</button>`
    : `<p style="color:var(--text3);font-size:12px;margin-top:12px">${esc(t('tenantSettings.readonly'))}</p>`;

  setContent(`
    <div class="card" style="max-width:560px">
      <p style="font-size:13px;color:var(--text3);margin-top:0;line-height:1.5">${esc(t('tenantSettings.intro'))}</p>

      <div class="form-group" style="margin-bottom:16px">
        <label style="font-weight:500;display:inline-flex;align-items:center;gap:8px">
          <span>${esc(t('tenantSettings.field.codeClient'))}</span>${helpTip('tenantSettings.field.codeClientHelp')}
        </label>
        <div style="margin-top:4px">
          <code style="display:inline-block;padding:6px 12px;background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);font-size:14px;font-weight:600;letter-spacing:1px">${esc(data.code_client || '—')}</code>
        </div>
      </div>

      ${numField('ts-ia-credits', 'tenantSettings.field.iaCredits', 'tenantSettings.field.iaCreditsHelp', data.ia_credits, 0)}
      ${numField('ts-retention', 'tenantSettings.field.retention', 'tenantSettings.field.retentionHelp', data.actions_retention_months, 1)}
      ${numField('ts-notif-retention', 'tenantSettings.field.notifRetention', 'tenantSettings.field.notifRetentionHelp', data.notifications_retention_days, 1)}
      ${numField('ts-max-users', 'tenantSettings.field.maxUsers', 'tenantSettings.field.maxUsersHelp', data.max_users, 1)}

      <div class="form-group" style="margin-bottom:16px">
        <label style="font-weight:500;display:inline-flex;align-items:center;gap:8px">
          <span>${esc(t('tenantSettings.field.monthStartDay'))}</span>${helpTip('tenantSettings.field.monthStartDayHelp')}
        </label>
        <div style="margin-top:4px">
          <select id="ts-month-start-day" ${disabled} style="${fieldStyle}">${dayOptions}</select>
        </div>
      </div>

      <h3 style="font-size:13px;margin:20px 0 8px 0;color:var(--text2)">${esc(t('tenantSettings.section.kpi'))}</h3>
      ${numField('ts-kpi-blocked', 'tenantSettings.field.kpiBlockedPct', 'tenantSettings.field.kpiBlockedPctHelp', data.kpi_blocked_orange_pct, 0)}
      ${numField('ts-kpi-progress', 'tenantSettings.field.kpiProgressPct', 'tenantSettings.field.kpiProgressPctHelp', data.kpi_progress_orange_pct, 0)}
      ${numField('ts-kpi-reeval', 'tenantSettings.field.kpiReevalWeeks', 'tenantSettings.field.kpiReevalWeeksHelp', data.kpi_reeval_stable_weeks, 1)}

      <h3 style="font-size:13px;margin:20px 0 8px 0;color:var(--text2)">${esc(t('tenantSettings.section.committeeAlerts'))}</h3>
      <div class="form-group" style="margin-bottom:16px">
        <label style="font-weight:500;display:flex;align-items:center;gap:8px;cursor:pointer">
          <input type="checkbox" id="ts-committee-alerts"${data.committee_alerts_enabled ? ' checked' : ''} ${disabled}>
          <span>${esc(t('tenantSettings.field.committeeAlerts'))}</span>
          ${helpTip('tenantSettings.field.committeeAlertsHelp')}
        </label>
      </div>

      <h3 style="font-size:13px;margin:20px 0 8px 0;color:var(--text2)">${esc(t('tenantSettings.section.contract'))}</h3>
      <p style="font-size:12px;color:var(--text3);margin:0 0 12px 0;line-height:1.5">${esc(t('tenantSettings.contract.intro'))}</p>
      ${roField('tenantSettings.field.companyName', data.company_name)}
      ${roField('tenantSettings.field.contractContactName', data.contract_contact_name)}
      ${roField('tenantSettings.field.contractContactEmail', data.contract_contact_email)}

      ${saveBtn}
    </div>`);

  const btn = document.getElementById('btn-ts-save') as HTMLButtonElement | null;
  if (!btn) return;
  btn.addEventListener('click', () => {
    const iaCreditsEl = document.getElementById('ts-ia-credits') as HTMLInputElement | null;
    const retentionEl = document.getElementById('ts-retention') as HTMLInputElement | null;
    const notifRetentionEl = document.getElementById(
      'ts-notif-retention',
    ) as HTMLInputElement | null;
    const maxUsersEl = document.getElementById('ts-max-users') as HTMLInputElement | null;
    const dayEl = document.getElementById('ts-month-start-day') as HTMLSelectElement | null;
    const kpiBlockedEl = document.getElementById('ts-kpi-blocked') as HTMLInputElement | null;
    const kpiProgressEl = document.getElementById('ts-kpi-progress') as HTMLInputElement | null;
    const kpiReevalEl = document.getElementById('ts-kpi-reeval') as HTMLInputElement | null;
    const committeeAlertsEl = document.getElementById(
      'ts-committee-alerts',
    ) as HTMLInputElement | null;

    const iaCredits = parseInt(iaCreditsEl?.value || '', 10);
    const retention = parseInt(retentionEl?.value || '', 10);
    const notifRetention = parseInt(notifRetentionEl?.value || '', 10);
    const maxUsers = parseInt(maxUsersEl?.value || '', 10);
    const monthStartDay = parseInt(dayEl?.value || '', 10);
    const kpiBlocked = parseInt(kpiBlockedEl?.value || '', 10);
    const kpiProgress = parseInt(kpiProgressEl?.value || '', 10);
    const kpiReeval = parseInt(kpiReevalEl?.value || '', 10);

    if (!Number.isInteger(iaCredits) || iaCredits < 0) {
      toast(t('tenantSettings.err.iaCredits'), 'error');
      return;
    }
    if (!Number.isInteger(retention) || retention < 1) {
      toast(t('tenantSettings.err.retention'), 'error');
      return;
    }
    if (!Number.isInteger(notifRetention) || notifRetention < 1) {
      toast(t('tenantSettings.err.notifRetention'), 'error');
      return;
    }
    if (!Number.isInteger(maxUsers) || maxUsers < 1) {
      toast(t('tenantSettings.err.maxUsers'), 'error');
      return;
    }
    if (
      !Number.isInteger(kpiBlocked) ||
      kpiBlocked < 0 ||
      kpiBlocked > 100 ||
      !Number.isInteger(kpiProgress) ||
      kpiProgress < 0 ||
      kpiProgress > 100 ||
      !Number.isInteger(kpiReeval) ||
      kpiReeval < 1 ||
      kpiReeval > 52
    ) {
      toast(t('tenantSettings.err.kpi'), 'error');
      return;
    }

    void api.withButton(btn, async () => {
      try {
        await api.updateTenantSettings({
          ia_credits: iaCredits,
          actions_retention_months: retention,
          notifications_retention_days: notifRetention,
          max_users: maxUsers,
          month_start_day: monthStartDay,
          kpi_blocked_orange_pct: kpiBlocked,
          kpi_progress_orange_pct: kpiProgress,
          kpi_reeval_stable_weeks: kpiReeval,
          committee_alerts_enabled: !!committeeAlertsEl?.checked,
        });
        toast(t('tenantSettings.toast.saved'));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    });
  });
}

// ── ESM globals shim ──
window.renderTenantSettingsPage = renderTenantSettingsPage;

export {};
